<template>
  <div
    v-if="visible"
    class="toast"
    :class="toastClass"
    role="alert"
    aria-live="polite"
  >
    <span
      v-if="showIcon"
      class="toast__icon"
    >
      <InfoIcon icon-color="#ffffff" />
    </span>

    <div class="toast__body">
      <div class="toast__content">
        <div class="toast__text">
          <h3
            v-if="title"
            class="toast__title"
          >
            {{ title }}
          </h3>

          <p
            v-if="showMessage"
            class="toast__message"
          >
            {{ message }}
          </p>
        </div>

        <div
          v-if="showSuffix"
          class="toast__suffix"
        >
          <component
            :is="buttonTag"
            class="toast__button"
            :href="buttonHref || undefined"
            :type="buttonHref ? undefined : 'button'"
            :target="buttonHref ? '_blank' : undefined"
            :rel="buttonHref ? 'noopener noreferrer' : undefined"
            @click="onButtonClick"
          >
            {{ buttonText }}
          </component>
        </div>
      </div>

      <div
        v-if="showFooter"
        class="toast__footer"
      >
        <component
          :is="actionTag"
          v-if="actionText"
          class="toast__action"
          :href="actionHref || undefined"
          :type="actionHref ? undefined : 'button'"
          :target="actionHref ? '_blank' : undefined"
          :rel="actionHref ? 'noopener noreferrer' : undefined"
          @click="onActionClick"
        >
          {{ actionText }}
        </component>

        <span
          v-if="errorCode"
          class="toast__code"
        >
          {{ errorCode }}
        </span>
      </div>
    </div>

    <button
      v-if="showCloseButton"
      type="button"
      class="toast__close"
      aria-label="Dismiss notification"
      @click="close"
    >
      <CrossIcon
        icon-color="#ffffff"
        :size="{ width: 18, height: 18 }"
      />
    </button>
  </div>
</template>

<script setup>
import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';
import { useToast } from '@/composables/ui/alerts/useToast';
import CrossIcon from '@/components/icons/Cross.vue';
import InfoIcon from '@/components/icons/Info.vue';

const { removeToast } = useToast();

const emit = defineEmits(['close', 'action', 'button']);

const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'minimal',
    validator: (value) => [
      'default',
      'horizontal',
      'minimal',
    ].includes(value),
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => [
      'info',
      'success',
      'error',
      'warning',
    ].includes(value),
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  actionText: {
    type: String,
    default: '',
  },
  actionHref: {
    type: String,
    default: '',
  },
  buttonText: {
    type: String,
    default: '',
  },
  buttonHref: {
    type: String,
    default: '',
  },
  errorCode: {
    type: String,
    default: '',
  },
  duration: {
    type: Number,
    default: 3000,
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: 'top-right',
    validator: (value) => [
      'top-left',
      'top-right',
      'top-center',
      'bottom-left',
      'bottom-right',
      'bottom-center',
    ].includes(value),
  },
  id: {
    type: Number,
    default: 0,
  },
});

const visible = ref(false);
let timer = null;

const isDefault = computed(() => props.variant === 'default');
const isHorizontal = computed(() => props.variant === 'horizontal');
const isMinimal = computed(() => props.variant === 'minimal');

const showMessage = computed(() => !isDefault.value && Boolean(props.message));
const showCloseButton = computed(() => props.showClose);
const showFooter = computed(
  () => isMinimal.value && Boolean(props.actionText || props.errorCode),
);
const showSuffix = computed(() => isHorizontal.value && Boolean(props.buttonText));

const toastClass = computed(() => ({
  [`toast--${props.type}`]: true,
  [`toast--${props.variant}`]: true,
}));

const actionTag = computed(() => (props.actionHref ? 'a' : 'button'));
const buttonTag = computed(() => (props.buttonHref ? 'a' : 'button'));

const close = () => {
  visible.value = false;
  if (timer) {
    clearTimeout(timer);
    removeToast(props.id);
    timer = null;
  }
  emit('close');
};

const onActionClick = (event) => {
  if (props.actionHref) return;
  event.preventDefault();
  emit('action');
};

const onButtonClick = (event) => {
  if (props.buttonHref) return;
  event.preventDefault();
  emit('button');
};

onMounted(() => {
  visible.value = true;
  if (props.duration > 0) {
    timer = setTimeout(close, props.duration);
  }
});

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>

<style lang="scss" scoped>
$toast-default-bg: #111214;
$toast-info-bg: #2563eb;
$toast-success-bg: #16a34a;
$toast-error-bg: #dc2626;
$toast-warning-bg: #ea580c;
$toast-text: #ffffff;
$toast-muted: #ffffff7a;
$toast-border: #ffffff14;
$toast-cta-bg: #f8f9fa;
$toast-cta-fg: #111214;

.toast {
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  width: 100%;
  padding: 2rem;
  border-radius: 1.2rem;
  color: $toast-text;
  background: $toast-default-bg;
  border: 1px solid $toast-border;
  box-shadow: $main-shadow;

  &__icon {
    flex-shrink: 0;
    display: flex;
    width: 2rem;
    height: 2rem;

    :deep(svg) {
      width: 100%;
      height: 100%;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.2rem;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  &__title {
    margin: 0;
    font-size: $font-body;
    font-weight: 600;
    line-height: 1.3;
    color: $toast-text;
  }

  &__message {
    margin: 0;
    font-size: $font-body;
    font-weight: 400;
    line-height: 1.4;
    color: $toast-text;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
    margin-top: 1.6rem;
  }

  &__suffix {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.8rem 1.6rem;
    font-size: $font-body-small;
    font-weight: 500;
    line-height: 1.4;
    white-space: nowrap;
    color: $toast-cta-fg;
    background: $toast-cta-bg;
    border: none;
    border-radius: 0.8rem;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }

  &__action {
    margin: 0;
    padding: 0;
    font-size: $font-body;
    font-weight: 400;
    line-height: 1.4;
    color: $toast-text;
    text-decoration: underline;
    text-underline-offset: 0.2rem;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.85;
    }
  }

  &__code {
    flex-shrink: 0;
    font-size: $font-body-small;
    font-weight: 400;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $toast-muted;
  }

  &__close {
    flex-shrink: 0;
    align-self: flex-start;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 50%;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  &--default,
  &--horizontal {
    align-items: center;
  }

  &--horizontal &__content {
    align-items: center;
  }

  &--info {
    background: $toast-info-bg;
    border-color: transparent;
  }

  &--success {
    background: $toast-success-bg;
    border-color: transparent;
  }

  &--error {
    background: $toast-error-bg;
    border-color: transparent;
  }

  &--warning {
    background: $toast-warning-bg;
    border-color: transparent;
  }
}
</style>
